/**
 * RestoID — MFA passkey (WebAuthn) verification.
 * ---------------------------------------------------------------------------
 * RestoID frame (hero + concise copy) around the PRISTINE upstream WebAuthn
 * verification logic (`useWebAuthnOperation`, verification-id lookup) + the
 * RestoID switch-method link. Swapped in for
 * `./pages/MfaVerification/WebAuthnVerification` via `src/App.tsx`.
 */
import { VerificationType } from '@logto/schemas';
import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useWebAuthnOperation from '@/hooks/use-webauthn-operation';
import { UserMfaFlow } from '@/types';
import { webAuthnStateGuard } from '@/types/guard';
import { isWebAuthnOptions } from '@/utils/webauthn';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import { Button, HeroTile, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const MfaWebAuthnVerification = () => {
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { state } = useLocation();
  const [, webAuthnState] = validate(state, webAuthnStateGuard);
  const { verificationIdsMap } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[VerificationType.WebAuthn];

  const handleWebAuthn = useWebAuthnOperation();
  const [isVerifying, setIsVerifying] = useState(false);

  if (!webAuthnState || !verificationId) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { options, availableFactors, skippable } = webAuthnState;

  if (!isWebAuthnOptions(options)) {
    return <ErrorPage title="error.invalid_session" />;
  }

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.verify_via_passkey' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={c('mfaVerifyPasskeyTitle')}
          sub={c('mfaVerifyPasskeyDesc')}
        />
        <HeroTile icon="finger" />
        <Button
          title={c('mfaVerifyPasskeyCta')}
          isLoading={isVerifying}
          onClick={async () => {
            setIsVerifying(true);
            await handleWebAuthn(options, verificationId);
            setIsVerifying(false);
          }}
        />
        <MfaSwitchLink
          flow={UserMfaFlow.MfaVerification}
          flowState={{ availableFactors, skippable }}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaWebAuthnVerification;
